'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// ─── Types ────────────────────────────────────────────────────────────────────
type FilterTab = 'all' | 'streak' | 'habits' | 'groups';
type StreakTier = 'all' | 'beginner' | 'consistent' | 'dedicated' | 'champion';
type ConnectionStatus = 'none' | 'pending' | 'connected';

interface CommunityUser {
  id: string;
  display_name: string;
  avatar_initials: string;
  current_streak: number;
  longest_streak: number;
  prayers_completed: number;
  top_habits: string[];
  prayer_groups: string[];
  streak_tier: StreakTier;
  connection_status: ConnectionStatus;
  is_online: boolean;
  location?: string;
}

interface PrayerGroup {
  id: string;
  name: string;
  emoji: string;
  member_count: number;
  goal_prayers_per_day: number;
  description: string;
  avg_streak: number;
  is_joined: boolean;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_USERS: CommunityUser[] = [
  {
    id: 'u1', display_name: 'Fatima Al-Rashid', avatar_initials: 'FA',
    current_streak: 142, longest_streak: 142, prayers_completed: 710,
    top_habits: ['Fajr on time', 'Quran daily', 'Dhikr morning'],
    prayer_groups: ['Fajr Warriors', 'Quran Circle'],
    streak_tier: 'champion', connection_status: 'none', is_online: true, location: 'Riyadh',
  },
  {
    id: 'u2', display_name: 'Omar Hussain', avatar_initials: 'OH',
    current_streak: 98, longest_streak: 120, prayers_completed: 490,
    top_habits: ['5 prayers on time', 'Night prayer', 'Fasting Mondays'],
    prayer_groups: ['Night Prayers', 'Ramadan Prep'],
    streak_tier: 'champion', connection_status: 'connected', is_online: false, location: 'Cairo',
  },
  {
    id: 'u3', display_name: 'Aisha Noor', avatar_initials: 'AN',
    current_streak: 87, longest_streak: 87, prayers_completed: 435,
    top_habits: ['Quran daily', 'Dhikr morning', 'Charity weekly'],
    prayer_groups: ['Quran Circle', 'Sisters Halaqa'],
    streak_tier: 'dedicated', connection_status: 'pending', is_online: true, location: 'Karachi',
  },
  {
    id: 'u4', display_name: 'Yusuf Ibrahim', avatar_initials: 'YI',
    current_streak: 74, longest_streak: 90, prayers_completed: 370,
    top_habits: ['Fajr on time', 'Sunnah prayers', 'Istighfar daily'],
    prayer_groups: ['Fajr Warriors'],
    streak_tier: 'dedicated', connection_status: 'none', is_online: true, location: 'Lagos',
  },
  {
    id: 'u5', display_name: 'Maryam Khalid', avatar_initials: 'MK',
    current_streak: 61, longest_streak: 75, prayers_completed: 305,
    top_habits: ['5 prayers on time', 'Quran daily'],
    prayer_groups: ['Sisters Halaqa', 'Ramadan Prep'],
    streak_tier: 'dedicated', connection_status: 'none', is_online: false, location: 'Istanbul',
  },
  {
    id: 'u6', display_name: 'Hassan Al-Amin', avatar_initials: 'HA',
    current_streak: 55, longest_streak: 68, prayers_completed: 275,
    top_habits: ['Fajr on time', 'Dhikr morning'],
    prayer_groups: ['Fajr Warriors', 'Night Prayers'],
    streak_tier: 'consistent', connection_status: 'none', is_online: true, location: 'Nairobi',
  },
  {
    id: 'u7', display_name: 'Zainab Osman', avatar_initials: 'ZO',
    current_streak: 43, longest_streak: 55, prayers_completed: 215,
    top_habits: ['Quran daily', 'Charity weekly'],
    prayer_groups: ['Quran Circle'],
    streak_tier: 'consistent', connection_status: 'none', is_online: false, location: 'Mogadishu',
  },
  {
    id: 'u8', display_name: 'Bilal Siddiqui', avatar_initials: 'BS',
    current_streak: 38, longest_streak: 50, prayers_completed: 190,
    top_habits: ['5 prayers on time', 'Sunnah prayers'],
    prayer_groups: ['Ramadan Prep'],
    streak_tier: 'consistent', connection_status: 'none', is_online: true, location: 'Lahore',
  },
  {
    id: 'u9', display_name: 'Khadija Musa', avatar_initials: 'KM',
    current_streak: 18, longest_streak: 40, prayers_completed: 145,
    top_habits: ['Fajr on time', 'Istighfar daily'],
    prayer_groups: ['Sisters Halaqa'],
    streak_tier: 'beginner', connection_status: 'none', is_online: false, location: 'Accra',
  },
  {
    id: 'u10', display_name: 'Idris Abdi', avatar_initials: 'IA',
    current_streak: 12, longest_streak: 35, prayers_completed: 105,
    top_habits: ['5 prayers on time'],
    prayer_groups: [],
    streak_tier: 'beginner', connection_status: 'none', is_online: true, location: 'Kampala',
  },
];

const MOCK_GROUPS: PrayerGroup[] = [
  { id: 'g1', name: 'Fajr Warriors', emoji: '🌅', member_count: 48, goal_prayers_per_day: 5, description: 'Never miss Fajr again. Daily accountability and encouragement.', avg_streak: 62, is_joined: false },
  { id: 'g2', name: 'Quran Circle', emoji: '📖', member_count: 35, goal_prayers_per_day: 5, description: 'Combine daily Quran reading with consistent prayer habits.', avg_streak: 78, is_joined: true },
  { id: 'g3', name: 'Night Prayers', emoji: '🌙', member_count: 22, goal_prayers_per_day: 5, description: 'Tahajjud and Isha on time. For those who seek the night.', avg_streak: 91, is_joined: false },
  { id: 'g4', name: 'Sisters Halaqa', emoji: '🌸', member_count: 31, goal_prayers_per_day: 5, description: 'A warm sisterhood for spiritual growth and prayer consistency.', avg_streak: 55, is_joined: false },
  { id: 'g5', name: 'Ramadan Prep', emoji: '🌙', member_count: 67, goal_prayers_per_day: 5, description: 'Year-round preparation for Ramadan through consistent worship.', avg_streak: 44, is_joined: false },
];

// ─── Tier Config ──────────────────────────────────────────────────────────────
const TIER_CONFIG: Record<StreakTier, { label: string; emoji: string; color: string; bg: string; border: string; min: number; max: number | null }> = {
  all:       { label: 'All Levels', emoji: '🌟', color: 'text-foreground', bg: 'bg-muted', border: 'border-border', min: 0, max: null },
  beginner:  { label: 'Beginner', emoji: '🌱', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', min: 0, max: 29 },
  consistent:{ label: 'Consistent', emoji: '🔥', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', min: 30, max: 59 },
  dedicated: { label: 'Dedicated', emoji: '⭐', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', min: 60, max: 99 },
  champion:  { label: 'Champion', emoji: '👑', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', min: 100, max: null },
};

const HABIT_FILTERS = ['All Habits', 'Fajr on time', '5 prayers on time', 'Quran daily', 'Dhikr morning', 'Night prayer', 'Fasting Mondays', 'Charity weekly'];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function CommunityPage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [streakFilter, setStreakFilter] = useState<StreakTier>('all');
  const [habitFilter, setHabitFilter] = useState('All Habits');
  const [groupFilter, setGroupFilter] = useState('All Groups');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<CommunityUser[]>(MOCK_USERS);
  const [groups, setGroups] = useState<PrayerGroup[]>(MOCK_GROUPS);
  const [connections, setConnections] = useState<Record<string, ConnectionStatus>>({});
  const [joinedGroups, setJoinedGroups] = useState<Set<string>>(new Set(['g2']));
  const [loading, setLoading] = useState(false);

  // Load real data from Supabase if available
  useEffect(() => {
    if (!user) return;
    loadCommunityData();
  }, [user]);

  const loadCommunityData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .neq('id', user?.id)
        .limit(20);

      if (profiles && profiles.length > 0) {
        const mapped: CommunityUser[] = profiles.map((p, i) => {
          const streak = Math.floor(Math.random() * 150);
          let tier: StreakTier = 'beginner';
          if (streak >= 100) tier = 'champion';
          else if (streak >= 60) tier = 'dedicated';
          else if (streak >= 30) tier = 'consistent';
          const initials = (p.full_name || p.email || 'U')
            .split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
          return {
            id: p.id,
            display_name: p.full_name || p.email?.split('@')[0] || 'User',
            avatar_initials: initials,
            current_streak: streak,
            longest_streak: streak + Math.floor(Math.random() * 20),
            prayers_completed: streak * 5,
            top_habits: MOCK_USERS[i % MOCK_USERS.length].top_habits,
            prayer_groups: MOCK_USERS[i % MOCK_USERS.length].prayer_groups,
            streak_tier: tier,
            connection_status: 'none',
            is_online: Math.random() > 0.5,
            location: MOCK_USERS[i % MOCK_USERS.length].location,
          };
        });
        setUsers(mapped);
      }

      const { data: groupData } = await supabase
        .from('prayer_groups')
        .select('id, name, avatar_emoji, goal_prayers_per_day, description')
        .eq('is_public', true)
        .limit(10);

      if (groupData && groupData.length > 0) {
        const mappedGroups: PrayerGroup[] = groupData.map((g, i) => ({
          id: g.id,
          name: g.name,
          emoji: g.avatar_emoji || '🕌',
          member_count: 10 + Math.floor(Math.random() * 60),
          goal_prayers_per_day: g.goal_prayers_per_day || 5,
          description: g.description || 'A prayer group for consistent worshippers.',
          avg_streak: 30 + Math.floor(Math.random() * 70),
          is_joined: joinedGroups.has(g.id),
        }));
        setGroups(mappedGroups);
      }
    } catch {
      // fallback to mock data
    } finally {
      setLoading(false);
    }
  }, [user, supabase, joinedGroups]);

  // ─── Filtering ──────────────────────────────────────────────────────────────
  const filteredUsers = users.filter((u) => {
    const matchSearch = !searchQuery || u.display_name.toLowerCase().includes(searchQuery.toLowerCase()) || u.location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStreak = streakFilter === 'all' || u.streak_tier === streakFilter;
    const matchHabit = habitFilter === 'All Habits' || u.top_habits.includes(habitFilter);
    const matchGroup = groupFilter === 'All Groups' || u.prayer_groups.includes(groupFilter);
    if (activeTab === 'streak') return matchSearch && matchStreak;
    if (activeTab === 'habits') return matchSearch && matchHabit;
    if (activeTab === 'groups') return matchSearch && matchGroup;
    return matchSearch && matchStreak && matchHabit && matchGroup;
  });

  const allGroupNames = ['All Groups', ...Array.from(new Set(users.flatMap((u) => u.prayer_groups)))];

  // ─── Actions ────────────────────────────────────────────────────────────────
  const handleConnect = (userId: string) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? { ...u, connection_status: u.connection_status === 'none' ? 'pending' : u.connection_status }
          : u
      )
    );
  };

  const handleJoinGroup = (groupId: string) => {
    setJoinedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, is_joined: !g.is_joined, member_count: g.is_joined ? g.member_count - 1 : g.member_count + 1 }
          : g
      )
    );
  };

  // ─── Stats ──────────────────────────────────────────────────────────────────
  const connectedCount = users.filter((u) => u.connection_status === 'connected').length;
  const onlineCount = users.filter((u) => u.is_online).length;
  const joinedGroupCount = groups.filter((g) => g.is_joined).length;

  return (
    <AppLayout activeRoute="/community">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-2xl gradient-teal flex items-center justify-center">
            <Icon name="UsersIcon" size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-700 text-foreground">Muslim Community</h1>
            <p className="text-sm text-muted-foreground">Browse and connect with fellow worshippers</p>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="UserGroupIcon" size={18} className="text-primary" />
          </div>
          <div>
            <p className="text-lg font-700 text-foreground">{connectedCount}</p>
            <p className="text-xs text-muted-foreground">Connections</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 block" />
          </div>
          <div>
            <p className="text-lg font-700 text-foreground">{onlineCount}</p>
            <p className="text-xs text-muted-foreground">Online now</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Icon name="UserGroupIcon" size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="text-lg font-700 text-foreground">{joinedGroupCount}</p>
            <p className="text-xs text-muted-foreground">My Groups</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by name or location…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-none">
        {([
          { key: 'all', label: 'All Members', icon: 'UsersIcon' },
          { key: 'streak', label: 'By Streak', icon: 'FireIcon' },
          { key: 'habits', label: 'By Habits', icon: 'CheckCircleIcon' },
          { key: 'groups', label: 'Prayer Groups', icon: 'UserGroupIcon' },
        ] as { key: FilterTab; label: string; icon: string }[]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-500 whitespace-nowrap transition-all duration-150 ${
              activeTab === tab.key
                ? 'bg-primary text-white shadow-sm'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filter Pills */}
      {activeTab !== 'groups' && (
        <div className="flex flex-wrap gap-2 mb-5">
          {/* Streak tier filter */}
          {(activeTab === 'all' || activeTab === 'streak') && (
            <div className="flex gap-1.5 flex-wrap">
              {(Object.keys(TIER_CONFIG) as StreakTier[]).map((tier) => {
                const cfg = TIER_CONFIG[tier];
                return (
                  <button
                    key={tier}
                    onClick={() => setStreakFilter(tier)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-500 border transition-all duration-150 ${
                      streakFilter === tier
                        ? `${cfg.bg} ${cfg.color} ${cfg.border} shadow-sm`
                        : 'bg-card border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <span>{cfg.emoji}</span>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          )}
          {/* Habit filter */}
          {(activeTab === 'all' || activeTab === 'habits') && (
            <div className="flex gap-1.5 flex-wrap mt-1">
              {HABIT_FILTERS.map((h) => (
                <button
                  key={h}
                  onClick={() => setHabitFilter(h)}
                  className={`px-3 py-1.5 rounded-full text-xs font-500 border transition-all duration-150 ${
                    habitFilter === h
                      ? 'bg-primary/10 text-primary border-primary/30' :'bg-card border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Prayer Groups Tab */}
      {activeTab === 'groups' && (
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">{groups.length} groups available</p>
            <Link href="/prayer-groups" className="text-sm text-primary font-500 hover:underline flex items-center gap-1">
              Manage Groups <Icon name="ArrowRightIcon" size={14} />
            </Link>
          </div>
          {groups.map((group) => (
            <div key={group.id} className="bg-card border border-border rounded-2xl p-4 flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-2xl flex-shrink-0">
                {group.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-600 text-foreground text-sm">{group.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{group.description}</p>
                  </div>
                  <button
                    onClick={() => handleJoinGroup(group.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-600 transition-all duration-150 ${
                      group.is_joined
                        ? 'bg-muted text-muted-foreground hover:bg-red-50 hover:text-red-600'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {group.is_joined ? 'Leave' : 'Join'}
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="UsersIcon" size={12} />
                    {group.member_count} members
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="FireIcon" size={12} />
                    Avg {group.avg_streak}d streak
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="SunIcon" size={12} />
                    {group.goal_prayers_per_day} prayers/day
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Members Grid */}
      {activeTab !== 'groups' && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground">
              {filteredUsers.length} member{filteredUsers.length !== 1 ? 's' : ''} found
            </p>
            {loading && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="ArrowPathIcon" size={12} className="animate-spin" /> Loading…
              </span>
            )}
          </div>

          {filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon name="UsersIcon" size={28} className="text-muted-foreground" />
              </div>
              <p className="text-foreground font-500 mb-1">No members found</p>
              <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredUsers.map((member) => (
                <UserCard
                  key={member.id}
                  member={member}
                  onConnect={handleConnect}
                />
              ))}
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}

// ─── UserCard Component ───────────────────────────────────────────────────────
function UserCard({ member, onConnect }: { member: CommunityUser; onConnect: (id: string) => void }) {
  const tierCfg = TIER_CONFIG[member.streak_tier];

  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex flex-col gap-3 hover:shadow-card transition-shadow duration-150">
      {/* Top row */}
      <div className="flex items-start gap-3">
        <Link href={`/community/${member.id}`} className="relative flex-shrink-0 group">
          <div className="w-11 h-11 rounded-full gradient-teal flex items-center justify-center text-white font-600 text-sm group-hover:opacity-90 transition-opacity">
            {member.avatar_initials}
          </div>
          {member.is_online && (
            <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-card" />
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link href={`/community/${member.id}`} className="font-600 text-foreground text-sm truncate hover:text-primary transition-colors">
              {member.display_name}
            </Link>
            <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full border font-500 ${tierCfg.bg} ${tierCfg.color} ${tierCfg.border}`}>
              {tierCfg.emoji} {tierCfg.label}
            </span>
          </div>
          {member.location && (
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
              <Icon name="MapPinIcon" size={11} />
              {member.location}
            </p>
          )}
        </div>
      </div>

      {/* Streak stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-muted rounded-xl px-2 py-2 text-center">
          <p className="text-sm font-700 text-foreground">{member.current_streak}</p>
          <p className="text-xs text-muted-foreground leading-tight">Streak</p>
        </div>
        <div className="bg-muted rounded-xl px-2 py-2 text-center">
          <p className="text-sm font-700 text-foreground">{member.longest_streak}</p>
          <p className="text-xs text-muted-foreground leading-tight">Best</p>
        </div>
        <div className="bg-muted rounded-xl px-2 py-2 text-center">
          <p className="text-sm font-700 text-foreground">{member.prayers_completed}</p>
          <p className="text-xs text-muted-foreground leading-tight">Prayers</p>
        </div>
      </div>

      {/* Habits */}
      {member.top_habits.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {member.top_habits.slice(0, 3).map((habit) => (
            <span key={habit} className="text-xs bg-primary/8 text-primary px-2 py-0.5 rounded-full border border-primary/20">
              {habit}
            </span>
          ))}
        </div>
      )}

      {/* Groups */}
      {member.prayer_groups.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {member.prayer_groups.slice(0, 2).map((g) => (
            <span key={g} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full flex items-center gap-1">
              <Icon name="UserGroupIcon" size={10} />
              {g}
            </span>
          ))}
        </div>
      )}

      {/* Connect button */}
      <button
        onClick={() => onConnect(member.id)}
        disabled={member.connection_status !== 'none'}
        className={`w-full py-2 rounded-xl text-sm font-600 transition-all duration-150 flex items-center justify-center gap-2 ${
          member.connection_status === 'connected' ?'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
            : member.connection_status === 'pending' ?'bg-muted text-muted-foreground border border-border cursor-default' :'bg-primary text-white hover:bg-primary/90'
        }`}
      >
        <Icon
          name={
            member.connection_status === 'connected' ?'CheckCircleIcon'
              : member.connection_status === 'pending' ?'ClockIcon' :'UserPlusIcon'
          }
          size={16}
        />
        {member.connection_status === 'connected' ?'Connected'
          : member.connection_status === 'pending' ?'Request Sent' :'Connect'}
      </button>
    </div>
  );
}
